import { useEffect, useRef, useState } from 'react';
import Api from 'helpers/Api';
import { useQuery } from 'helpers/Url';
import { useLocation, useNavigate, Link, useParams } from 'react-router-dom';
import { Alert, Button, Table, FloatingLabel, Form, Row, Col, Container, InputGroup } from 'react-bootstrap';
import { ArrowLeftCircle, Rainbow, TrashFill, ArrowLeftRight, PlusLg } from 'react-bootstrap-icons';
import { useNestedState } from 'helpers/NestedState';
import { useCurrencyContext } from 'providers/Currency';
import { useValidation } from 'helpers/Validation';
import { useAuthDataContext } from 'providers/Auth';
import { cloneDeep } from 'lodash';

import Question from 'components/modals/Question';
import Layout from 'components/layout/Layout';
import Client from 'components/pages/clients/partials/AddOrEdit';
import Article from 'components/pages/articles/partials/AddOrEdit';
import Address from 'components/pages/clients/partials/Addresses';

//misc
import Loader from 'components/misc/Loader';
import moment from 'moment';
import Autocomplete from 'components/misc/Autocomplete';
import SaveButton from 'components/misc/Button';
import { addExtraPrice, calcDiscount, calcExtraPrice, convertFromRateToRate, isNumeric, removeExtraPrice, vatPrice } from 'helpers/Price';
import HelpInfo from 'components/misc/Info';
import Error from 'components/modals/Error';
import Info from 'components/modals/Error';
import Refs from 'Refs';
import DatePicker from 'components/partials/filter/DatePicker';
import PriceValue from 'components/partials/PriceValue';

let timeout;

function Index() {

    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const query = useQuery();

    const auth = useAuthDataContext();
    const currencies = useCurrencyContext();

    const [state, setState] = useNestedState({
        loading: false,
        loadingBase: false,
        loadingClose: false,
        dataLoading: false,
        data: {
            with_invoice: 0,
            currency_id: currencies.getDefault()?.id || '',
            currency_rate: currencies.getDefault()?.rate || '',
            currency: currencies.getDefault(),
            rows: [{}],
            allow_close: true,
            allow_edit_rows: true,
        },
        timeout: null
    });

    const [stores, setStores] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [speditors, setSpeditors] = useState([]);
    const [rowStatuses, setRowStatuses] = useState([]);
    const [addresses, setAddresses] = useState([]);

    const [validations, setValidations] = useValidation();

    const formRef = useRef(null);
    const addressModalRef = useRef(null);
    const questionModalRef = useRef(null);
    const errorModalRef = useRef(null);
    const infoModalRef = useRef(null);

    useEffect(() => {
        Api.get('orders/stores')
            .then(res => {
                if (Array.isArray(res.data)) {
                    setStores(res.data);
                }
            });

        Api.get('orders/payment-methods')
            .then(res => {
                if (Array.isArray(res.data)) {
                    setPaymentMethods(res.data);
                }
            });

        Api.get('orders/speditors')
            .then(res => {
                if (Array.isArray(res.data)) {
                    setSpeditors(res.data);
                }
            });

        Api.get('orders/row-statuses')
            .then(res => {
                if (Array.isArray(res.data)) {
                    setRowStatuses(res.data);
                }
            });
    }, []);

    useEffect(() => {
        if (params.id) {
            loadData();
        }
    }, [params.id]);

    // зареждане на данни за клинет
    useEffect(() => {
        if (params.id) {
            return;
        }

        // за клиент
        if (query.get('client_id')) {
            loadDataFromClient();
        }

        // от документ
        if (query.get('from_document_id')) {
            loadDataFromDocument();
        }
    }, [location]);

    useEffect(() => {
        if (state.data.rows.length === 0) {
            setState(prev => ({
                ...prev,
                data: {
                    ...prev.data,
                    rows: [{}]
                }
            }));
        }
    }, [state.data.rows]);

    const loadData = () => {

        loading(true);

        let url = 'orders/show';

        Api.get(url, {
            params: {
                id: params.id
            }
        }).then(res => {
            handleResponse(res);
        }).catch(err => {
            handleError(err);
        }).finally(() => {
            loading(false);
        })
    }

    const loadDataFromClient = () => {
        loading(true);

        let url = 'orders/from-client';

        Api.get(url, {
            params: {
                client_id: query.get('client_id'),
            }
        }).then(res => {
            handleResponse(res);
        }).catch(err => {
            handleError(err);
        }).finally(() => {
            loading(false);
        })
    }

    const loadDataFromDocument = () => {
        loading(true);

        let url = 'orders/from-document';

        Api.get(url, {
            params: {
                document_id: query.get('from_document_id'),
            }
        }).then(res => {
            handleResponse(res);
        }).catch(err => {
            handleError(err);
        }).finally(() => {
            loading(false);
        })
    }

    const handleResponse = res => {
        let data = res.data;

        let rows = data.rows || [];

        // data.currency_rate = data.currency?.rate;

        rows = rows.map(row => {
            row.article.currency = data.currency;

            return row;
        });

        data.rows = rows;

        setState(prev => ({
            ...prev,
            data: data,
        }));

        // възможните адреси на клиента за доставка
        setAddresses(data?.client?.addresses || []);
    }

    const handleError = error => {
        console.log(error);

        const _err = error.response;

        if (_err && _err.status) {
            if (_err.status === 422) {
                setValidations(_err.data.errors);
            } else {
                let modal = errorModalRef.current;

                if (modal) {
                    modal.open(_err.data.error || _err.data.message);

                    modal.onClose(() => {
                        navigate(-1, {
                            replace: true
                        });
                    });
                }
            }
        }
    }

    const loading = (loading) => {
        setState(prev => ({
            ...prev,
            dataLoading: Boolean(loading)
        }));
    }

    const handleInputChange = e => {
        const name = e.target.name
        const value = e.target.value

        setState(name, value, 'data')
    }

    const handleCheckboxChange = e => {
        const name = e.target.name;

        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: prev.data[name] ? false : true
            }
        }));
    }

    const handleCurrencyChange = e => {
        const name = e.target.name
        const value = e.target.value

        setCurrency(value);

    }

    const setCurrency = id => {
        let currency = currencies.data.find(c => Number(c.id) === Number(id));

        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                currency_id: id,
                currency: currency,
                currency_rate: currency ? currency.rate : prev.data.currency_rate,
            }
        }));

        if (state.data.rows.filter(r => r.article).length > 0) {
            let modal = questionModalRef.current;

            if (!modal) {
                return;
            }

            modal.open('Искате ли да се изчислят единичните цени на артикулите спрямо избраната валута?');

            modal.onSuccess(() => {
                setState(prev => ({
                    ...prev,
                    data: {
                        ...prev.data,
                        rows: prev.data.rows.map(r => {
                            let article = r.article;

                            if (article && article.currency) {
                                r.current_price = convertFromRateToRate(r.current_price, article.currency.rate, currency.rate);
                                r.article.currency = currency;
                            }

                            return r;
                        })
                    }
                }))
            });
        }
    }

    const handleDiscountChange = e => {
        let value = e.target.value;

        if (!value.includes('+') && !value.includes('-') && value.length > 0) {
            value = '-' + value;
        }

        if (value.includes('+')) {
            value = value.replace('-', '');
        }

        if (value < -100) {
            value = -100;
        }

        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                discount: value,
            }
        }));
    }

    const save = (close = false) => {
        if (state.loading) {
            return;
        }

        setState(prev => ({
            ...prev,
            loading: true,
            loadingBase: close ? false : true,
            loadingClose: close ? true : false,
        }));

        setValidations(null);

        let url = params.id ? 'orders/edit' : 'orders/add';

        let form = formRef.current;

        let data = new FormData(form);

        if (params.id) {
            data.append('id', params.id)
        }

        if (close) {
            data.append('close', 1);
        }

        Api.post(url, data, {
            headers: {
                'content-type': 'multipart/form-data'
            }
        }).then(res => {
            navigate('/orders');
        }).catch(error => {
            const _err = error.response;

            if (_err && _err.status) {
                if (_err.status === 422) {
                    setValidations(_err.data.errors);
                } else {
                    let modal = errorModalRef.current;

                    if (modal) {
                        modal.open(_err.data.error || _err.data.message);
                    }
                }
            }

        }).finally(() => {
            setState(prev => ({
                ...prev,
                loading: false,
                loadingBase: false,
                loadingClose: false,
            }));
        });
    }

    const handleSave = e => {
        e.preventDefault();

        save(false);
    }

    const handleSaveAndClose = e => {
        e.preventDefault();

        let modal = questionModalRef.current;

        if (modal) {
            modal.open();
            modal.onSuccess(() => {
                save(true);
            });
        }

    }

    const handleChangeClient = data => {

        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                client: data,
                client_id: data?.id,
                discount: Number(data?.discount),
                contact_name: data?.name,
                client_tel: data?.tel,
                client_email: data?.email,
                paymethod_id: data?.method_id,
            }
        }));

        setAddresses(data?.addresses || []);

        let address = null;

        if (data?.addresses) {
            address = data.addresses[0];
        }

        handleChangeAddress(address);

    }

    const handleAddClient = () => {
        let modal = Refs.getInstance().getRef('newClient');

        if (modal) {
            modal.add();
            modal.onSuccess(client => {
                if (client) {
                    handleChangeClient(client);
                }
            });
        }
    }

    const handleShowClient = id => {
        let modal = Refs.getInstance().getRef('client');

        if (modal) {
            modal.open(id);
        }
    }

    const handleChangeAddress = address => {
        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                deliveryaddress: address,
                delivery_address_id: address?.id,
                speditor_id: address?.speditor_id,
            }
        }));
    }

    const handleEditAddress = () => {
        if (!state.data.delivery_address_id) {
            return;
        }

        let modal = addressModalRef.current;

        if (modal) {
            modal.edit(state.data.delivery_address_id);

            modal.onSuccess(address => {
                if (address) {
                    handleChangeAddress(address);

                    // ако го има в списъка да се обнови...
                    setAddresses(prev => prev.map(a => {
                        if (Number(a.id) === Number(address.id)) {
                            a = address;
                        }

                        return a;
                    }));
                }
            });
        }
    }

    const handleAddAddress = (checkClient = true) => {
        if (!state.data.client_id) {
            if (checkClient) {
                let modal = infoModalRef.current;

                if (modal) {
                    modal.open('Изберете клиент');
                }
            }

            return;
        }

        let modal = addressModalRef.current;

        if (modal) {

            modal.add(state.data.client_id);

            modal.onSuccess(address => {
                if (address) {
                    handleChangeAddress(address);

                    // да го добавим в списъка
                    setAddresses(prev => prev.concat(address));
                }
            });
        }
    }

    const handleClickAddress = () => {
        if (addresses.length === 0) {
            handleAddAddress(false);
        }
    }

    const handleAddRow = e => {
        e.preventDefault()

        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                rows: prev.data.rows.concat({})
            }
        }))
    }

    const handleRemoveRow = (e, index) => {
        e.preventDefault()

        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                rows: prev.data.rows.filter((row, i) => i !== index)
            }
        }))
    }

    const handleChangeArticle = (data, index) => {

        let row = state.data.rows[index];

        let currency = data?.currency;

        data.currency = state.data.currency;

        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                rows: Object.values({
                    ...prev.data.rows,
                    [index]: {
                        ...prev.data.rows[index],
                        article: data,
                        article_id: data.id,
                        amount: data.amount_unit,
                        current_price: convertFromRateToRate(data.current_price || data.actual_price, currency?.rate, state.data.currency_rate),
                    }
                })
            }
        }));

    }

    const handleAddArticle = (index) => {
        let modal = Refs.getInstance().getRef('newArticle');

        if (modal) {
            modal.add();
            modal.onSuccess(article => {
                if (article) {
                    handleChangeArticle(article, index);
                }
            });
        }
    }

    const handleShowArticle = id => {
        let modal = Refs.getInstance().getRef('article');

        if (modal) {
            modal.open(id);
        }
    }

    // при смяна на количеството 
    const handleQtyChange = (e, index) => {
        handleInputChange(e);

        let value = e.target.value;

    }

    const getRowTotalPrice = row => {
        return (parseFloat(row.current_price || 0) * parseFloat(row.amount || 0));
    }

    const getTotalQty = () => {
        let total = 0;

        state.data.rows.map(row => {
            total += parseFloat(row.quantity || 0);
        });

        return total;
    }

    const getTotalSum = () => {
        let total = 0;

        state.data.rows.map(row => {
            total += getRowTotalPrice(row);
        });

        return total;
    }

    const getTotalDiscount = () => {
        return calcDiscount(getTotalSum(), state.data.discount);
    }

    const getTotalReal = () => {
        return getTotalSum() + getTotalDiscount();
    }

    const isDiscount = () => {
        return state.data.discount < 0;
    }

    const isDeliveryToAddress = () => {
        return !state.data.delivery_store_id || state.data.delivery_store_id === '';
    }

    const getAddressName = address => {
        if (!address) {
            return '';
        }

        let name = [];

        if (address.delivery_type === 'address') {
            name = [
                address.dest_name_full,
                address.address,
                address.description,
                address.country_name,
            ];
        }

        if (address.delivery_type === 'office') {
            name = [
                address.dest_name_full,
                address.office_name,
            ];
        }

        if (address.delivery_type === 'store') {
            name = [
                address?.store?.translation?.name || address?.store?.name,
            ];
        }

        return name.filter(p => p).join(', ');
    }

    const getExtendedAddressName = address => {
        if (!address) {
            return '';
        }

        let map = {
            address: 'Адрес на клиент',
            office: 'Офис на куриер',
            store: 'Търговски обект'
        };

        let type = map[address.delivery_type];

        return type + ': ' + getAddressName(address);
    }

    const handleChangeStore = data => {
        let checked = data.map(d => d.id);

        // props.handleSearch('store_id', checked);
    }

    return (
        <>
            <Address
                ref={addressModalRef}
            />

            <Question
                ref={questionModalRef}
            />

            <Error
                ref={errorModalRef}
            />

            <Info
                ref={infoModalRef}
            />

            <Layout>
                <div className="container-fluid">

                    <div className="page-head">
                        <h1 className="display-6">
                            <Link to={-1} className="link-dark text-decoration-none">
                                <ArrowLeftCircle />
                            </Link> {params.id ? 'Редактиране на клиентска заявка' : 'Нова клиентска заявка'}
                        </h1>

                        <div className="buttons">

                        </div>
                    </div>

                    <br />

                    <div className="panel">

                        {state.dataLoading
                            ?
                            <Loader />
                            :
                            <>
                                <Form ref={formRef}>
                                    {/* <Container> */}
                                    <div className="form-panel">
                                        <div className="head">
                                            Данни за заявката
                                        </div>
                                        <Row>
                                            <Col className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Заявка №
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder=""
                                                            name="ref"
                                                            value={state.data.id || ''}
                                                            readOnly
                                                            size="sm"
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        За дата
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <DatePicker
                                                            variant="basic"
                                                            type="datetime-local"
                                                            name="delivery_date"
                                                            value={state.data?.delivery_date ? moment(state.data.delivery_date).format('YYYY-MM-DDTHH:mm') : ''}
                                                            onChange={handleInputChange}
                                                            isInvalid={Boolean(validations?.delivery_date)}
                                                            size="sm"
                                                            error={validations?.delivery_date && (validations.delivery_date[0] || validations.delivery_date)}
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Интервал до
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <DatePicker
                                                            variant="basic"
                                                            type="datetime-local"
                                                            name="delivery_date_until"
                                                            value={state.data?.delivery_date_until ? moment(state.data.delivery_date_until).format('YYYY-MM-DDTHH:mm') : ''}
                                                            onChange={handleInputChange}
                                                            isInvalid={Boolean(validations?.delivery_date_until)}
                                                            size="sm"
                                                            error={validations?.delivery_date_until && (validations.delivery_date_until[0] || validations.delivery_date_until)}
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Клиент
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Autocomplete
                                                            variant="basic"
                                                            url="orders/clients"
                                                            inputName="client_name"
                                                            inputIdName="client_id"
                                                            inputValue={state.data.client_name || state.data?.client?.name}
                                                            inputIdValue={state.data?.client_id || ''}
                                                            onChange={handleChangeClient}
                                                            onClickAddButton={handleAddClient}
                                                            onClickShowButton={handleShowClient}
                                                            addButtonDisabled={auth.getUser().permission('clients_edit') === false}
                                                            error={Boolean(validations?.client_id)}
                                                            helperText={validations?.client_id && (validations.client_id[0] || validations.client_id)}
                                                            renderText={data => {
                                                                return (
                                                                    <div style={{ display: 'flex', width: '100%' }}>
                                                                        <div style={{ width: 'max-content', marginRight: '10px' }}>
                                                                            {data.id}
                                                                        </div>
                                                                        <div>
                                                                            {data.name}
                                                                            <br />
                                                                            {data.email}
                                                                        </div>
                                                                    </div>
                                                                )
                                                            }}
                                                            renderInputText={data => data.name}
                                                            size="sm"
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Отстъпка/Надценка <HelpInfo>минус - отстъпка в проценти <br />плюс - надценка в проценти</HelpInfo>
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Control
                                                            type="text"
                                                            step="0.01"
                                                            placeholder=""
                                                            name="discount"
                                                            value={state.data?.discount || ''}
                                                            onChange={handleDiscountChange}
                                                            size="sm"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.discount && (validations.discount[0] || validations.discount)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Начин на плащане
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Select
                                                            name="paymethod_id"
                                                            value={state.data?.paymethod_id || ''}
                                                            onChange={handleInputChange}
                                                            isInvalid={Boolean(validations?.paymethod_id)}
                                                            size="sm"
                                                        >
                                                            <option value=""></option>
                                                            {paymentMethods.map(o =>
                                                                <option key={o.id} value={o.id}>{o?.translation?.name || o.name}</option>
                                                            )}

                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.paymethod_id && (validations.paymethod_id[0] || validations.paymethod_id)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Лице за контакти
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Control
                                                            placeholder=""
                                                            name="contact_name"
                                                            value={state.data?.contact_name || ''}
                                                            onChange={handleInputChange}
                                                            size="sm"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.contact_name && (validations.contact_name[0] || validations.contact_name)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Телефон
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Control
                                                            placeholder=""
                                                            name="client_tel"
                                                            value={state.data?.client_tel || ''}
                                                            onChange={handleInputChange}
                                                            size="sm"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.client_tel && (validations.client_tel[0] || validations.client_tel)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Е-поща
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Control
                                                            placeholder=""
                                                            name="client_email"
                                                            value={state.data?.client_email || ''}
                                                            onChange={handleInputChange}
                                                            size="sm"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.client_email && (validations.client_email[0] || validations.client_email)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Доставка до
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Select
                                                            name="delivery_store_id"
                                                            value={state.data?.delivery_store_id || ''}
                                                            onChange={handleInputChange}
                                                            isInvalid={Boolean(validations?.delivery_store_id)}
                                                            size="sm"
                                                        >
                                                            <option value="">Адрес на клиента</option>
                                                            {stores.map(o =>
                                                                <option key={o.id} value={o.id}>{o?.translation?.name || o.name}</option>
                                                            )}

                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.delivery_store_id && (validations.delivery_store_id[0] || validations.delivery_store_id)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Спедитор
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Select
                                                            name="speditor_id"
                                                            value={state.data?.speditor_id || ''}
                                                            onChange={handleInputChange}
                                                            isInvalid={Boolean(validations?.speditor_id)}
                                                            size="sm"
                                                        >
                                                            <option value="">Няма</option>
                                                            {speditors.map(o =>
                                                                <option key={o.id} value={o.id}>{o?.translation?.name || o.name}</option>
                                                            )}

                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.speditor_id && (validations.speditor_id[0] || validations.speditor_id)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Товарителница №
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Control
                                                            placeholder=""
                                                            name="tracking_number"
                                                            value={state.data?.tracking_number || ''}
                                                            onChange={handleInputChange}
                                                            size="sm"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.tracking_number && (validations.tracking_number[0] || validations.tracking_number)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Данни за доставка
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        {/* <InputGroup size="sm">
                                                                <Form.Select
                                                                    name="delivery_address_id"
                                                                    value={state.data?.delivery_address_id || ''}
                                                                    onChange={handleInputChange}
                                                                    size="sm"
                                                                >
                                                                    {addresses.map(address =>
                                                                        <option
                                                                            key={address.id}
                                                                            value={address.id}
                                                                        >
                                                                            {getAddressName(address)}
                                                                        </option>
                                                                    )}
                                                                </Form.Select>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={handleAddAddress}
                                                                >
                                                                    <PlusLg />
                                                                </Button>
                                                            </InputGroup> */}
                                                        <Autocomplete
                                                            variant="basic"
                                                            data={addresses}
                                                            filterable={false}
                                                            inputValue={isDeliveryToAddress() ? getAddressName(state.data?.deliveryaddress) : ''}
                                                            inputIdName={`delivery_address_id`}
                                                            inputIdValue={state.data?.delivery_address_id || ''}
                                                            onChange={data => handleChangeAddress(data)}
                                                            onClickAddButton={e => handleAddAddress()}
                                                            onClick={e => handleClickAddress()}
                                                            error={Boolean(validations && validations.delivery_address_id && (validations.delivery_address_id[0] || validations.delivery_address_id))}
                                                            helperText={validations && validations.delivery_address_id && (validations.delivery_address_id[0] || validations.delivery_address_id)}
                                                            renderText={data => getExtendedAddressName(data)}
                                                            renderInputText={data => getAddressName(data)}
                                                            inputDisabled={isDeliveryToAddress() === false}
                                                            size="sm"
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        С фактура <HelpInfo>Да се издаде фактура след приключване на сметката, на база клиентската заявка.</HelpInfo>
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <div className="custom-radio">
                                                            <label>
                                                                <input type="radio" name="with_invoice" value={0} checked={Number(state.data.with_invoice) === 0} onChange={handleInputChange} />
                                                                <span>
                                                                    НЕ
                                                                </span>
                                                            </label>
                                                            <label>
                                                                <input type="radio" name="with_invoice" value={1} checked={Number(state.data.with_invoice) === 1} onChange={handleInputChange} />
                                                                <span>
                                                                    ДА
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        Валута
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Form.Select
                                                            name="currency_id"
                                                            value={state.data?.currency_id || ''}
                                                            onChange={handleCurrencyChange}
                                                            isInvalid={Boolean(validations?.currency_id)}
                                                            size="sm"
                                                        >
                                                            {currencies.data.map(o =>
                                                                <option key={o.id} value={o.id}>{o?.translation?.name || o.name}</option>
                                                            )}

                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            {validations?.currency_id && (validations.currency_id[0] || validations.currency_id)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={4} className="mb-3">
                                                <Form.Group as={Row}>
                                                    <Form.Label column="sm" sm={4} className="text-left">
                                                        За обработка в
                                                    </Form.Label>
                                                    <Col sm={8}>
                                                        <Autocomplete
                                                            variant="basic"
                                                            size="sm"
                                                            url="autocomplete/stores"
                                                            inputIdName="store_id"
                                                            // selectedIds={[]}
                                                            selectedValues={state.data.stores || []}
                                                            renderText={data => data?.translation?.name || data?.name}
                                                            onChange={data => handleChangeStore(data)}
                                                            multiple
                                                            filterable={false}
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </div>


                                    <div className="form-panel mt-3">
                                        <div className="head">
                                            Артикули
                                        </div>
                                        <Table className="valign-top big">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '5%' }}>
                                                        #
                                                    </th>
                                                    <th style={{ width: '25%' }}>
                                                        Артикул
                                                    </th>
                                                    <th style={{ width: '10%' }}>
                                                        Количество
                                                    </th>
                                                    <th style={{ width: '10%' }}>
                                                        Единична цена
                                                    </th>
                                                    <th style={{ width: '10%' }}>
                                                        Сума
                                                    </th>
                                                    <th style={{ width: '15%' }}>
                                                        Статус
                                                    </th>
                                                    <th style={{ width: '20%' }}>
                                                        Бележки
                                                    </th>
                                                    <th className="options">

                                                    </th>
                                                </tr>

                                            </thead>
                                            {state.data.allow_edit_rows
                                                ?
                                                <tbody>
                                                    {state.data.rows.map((row, index) =>
                                                        <tr key={'r-' + index}>
                                                            <td title={row.id || '-'} style={{ width: '5%' }}>
                                                                {index + 1}

                                                                <input type="hidden" name={`rows[${index}][id]`} value={state.data.rows[index].id || ''} />
                                                            </td>
                                                            <td style={{ width: '25%' }}>
                                                                <Autocomplete
                                                                    variant="basic"
                                                                    url="autocomplete/articles"
                                                                    params={{
                                                                        client_id: state.data.client_id,
                                                                    }}
                                                                    inputName="article_name"
                                                                    inputIdName={`rows[${index}][article_id]`}
                                                                    inputValue={state.data?.rows[index] && state.data?.rows[index]?.article?.name ? state.data?.rows[index]?.article?.name : ''}
                                                                    inputIdValue={state.data?.rows[index] && state.data?.rows[index].article_id ? state.data?.rows[index].article_id : ''}
                                                                    onChange={data => handleChangeArticle(data, index)}
                                                                    onClickAddButton={e => handleAddArticle(index)}
                                                                    onClickShowButton={handleShowArticle}
                                                                    error={Boolean(validations && validations.rows && validations.rows[index] && validations.rows[index].article_id && (validations.rows[index].article_id[0] || validations.rows[index].article_id))}
                                                                    helperText={validations && validations.rows && validations.rows[index] && validations.rows[index].article_id && (validations.rows[index].article_id[0] || validations.rows[index].article_id)}
                                                                    renderText={data => {
                                                                        return (
                                                                            <div style={{ display: 'flex', width: '100%' }}>
                                                                                <div style={{ width: 'max-content', marginRight: '10px' }}>
                                                                                    {data.id}
                                                                                </div>
                                                                                <div>
                                                                                    {data.name}
                                                                                    <br />
                                                                                    {data.article_name}
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    }}
                                                                    renderInputText={data => data.article_name}
                                                                    size="sm"
                                                                    listStyle={{
                                                                        minWidth: 'max-content'
                                                                    }}
                                                                    autoFocus
                                                                    addButtonDisabled={auth.getUser().permission('articles_edit') === false}
                                                                />
                                                            </td>

                                                            <td style={{ width: '10%' }}>
                                                                <InputGroup size="sm" style={{ minWidth: '95px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.0001"
                                                                        name={`rows[${index}][amount]`}
                                                                        value={state.data?.rows[index] && state.data?.rows[index].amount ? state.data?.rows[index].amount : ''}
                                                                        onChange={handleInputChange}
                                                                        isInvalid={Boolean(validations && validations.rows && validations.rows[index] && validations.rows[index].amount && (validations.rows[index].amount[0] || validations.rows[index].amount))}
                                                                        size="sm"
                                                                        className="price-input"
                                                                    />

                                                                    <InputGroup.Text>
                                                                        {state.data?.rows[index]?.article?.amount_type_short || '-'}
                                                                    </InputGroup.Text>

                                                                    {/* <Form.Control.Feedback type="invalid">
                                                                        {validations && validations.rows && validations.rows[index] && validations.rows[index].amount && (validations.rows[index].amount[0] || validations.rows[index].amount)}
                                                                    </Form.Control.Feedback> */}
                                                                </InputGroup>

                                                            </td>
                                                            <td style={{ width: '10%' }}>
                                                                <InputGroup size="sm" style={{ minWidth: '95px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.0001"
                                                                        name={`rows[${index}][current_price]`}
                                                                        value={state.data?.rows[index].current_price}
                                                                        onChange={handleInputChange}
                                                                        isInvalid={Boolean(validations && validations.rows && validations.rows[index] && validations.rows[index].current_price && (validations.rows[index].current_price[0] || validations.rows[index].current_price))}
                                                                        size="sm"
                                                                        className={`price-input`}
                                                                    />

                                                                    {state.data?.currency &&
                                                                        <InputGroup.Text>
                                                                            {state.data.currency.prefix || state.data.currency.sufix || '-'}/{state.data?.rows[index]?.article?.amount_type_short || '-'}
                                                                        </InputGroup.Text>
                                                                    }
                                                                </InputGroup>
                                                            </td>
                                                            <td style={{ width: '10%' }}>
                                                                <InputGroup size="sm" style={{ minWidth: '95px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.0001"
                                                                        value={getRowTotalPrice(state.data?.rows[index])}
                                                                        size="sm"
                                                                        className={`price-input  ${getRowTotalPrice(state.data?.rows[index]) < 0 && 'negative-value'}`}
                                                                        disabled
                                                                    />

                                                                    {state.data?.currency &&
                                                                        <InputGroup.Text>
                                                                            {state.data.currency.prefix || state.data.currency.sufix || '-'}
                                                                        </InputGroup.Text>
                                                                    }
                                                                </InputGroup>
                                                            </td>
                                                            <td style={{ width: '15%' }}>
                                                                <Form.Select
                                                                    name={`rows[${index}][status_id]`}
                                                                    value={state.data?.rows[index] && state.data?.rows[index].status_id ? state.data?.rows[index].status_id : ''}
                                                                    onChange={handleInputChange}
                                                                    isInvalid={Boolean(validations && validations.rows && validations.rows[index] && validations.rows[index].status_id && (validations.rows[index].status_id[0] || validations.rows[index].status_id))}
                                                                    size="sm"
                                                                    readOnly={auth.getUser().permission('clientorders_detail_status_change') === false}
                                                                >
                                                                    <option value=""></option>
                                                                    {rowStatuses.map(o =>
                                                                        <option key={o.id} value={o.id}>{o?.translation?.name || o.name}</option>
                                                                    )}

                                                                </Form.Select>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {validations && validations.rows && validations.rows[index] && validations.rows[index].status_id && (validations.rows[index].status_id[0] || validations.rows[index].status_id)}
                                                                </Form.Control.Feedback>
                                                            </td>
                                                            <td style={{ width: '20%' }}>
                                                                <Form.Control
                                                                    type="text"
                                                                    name={`rows[${index}][notes]`}
                                                                    value={state.data?.rows[index] && state.data?.rows[index].notes ? state.data?.rows[index].notes : ''}
                                                                    onChange={handleInputChange}
                                                                    isInvalid={Boolean(validations && validations.rows && validations.rows[index] && validations.rows[index].notes && (validations.rows[index].notes[0] || validations.rows[index].notes))}
                                                                    size="sm"
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {validations && validations.rows && validations.rows[index] && validations.rows[index].notes && (validations.rows[index].notes[0] || validations.rows[index].notes)}
                                                                </Form.Control.Feedback>
                                                            </td>
                                                            <td className="options">
                                                                <Button variant="danger" size="sm" onClick={e => handleRemoveRow(e, index)}>
                                                                    <TrashFill />
                                                                </Button>
                                                            </td>

                                                        </tr>
                                                    )}
                                                    {/* <tr>
                                                    <td colSpan={4}>
                                                        Общо
                                                    </td>
                                                    <td className="text-right">
                                                        {getTotalQty().toFixed(4)}
                                                    </td>
                                                </tr> */}

                                                </tbody>
                                                :
                                                <tbody>
                                                    {state.data.rows.map((row, index) =>
                                                        <tr key={'r-' + index}>
                                                            <td title={row.id || '-'} style={{ width: '5%' }}>
                                                                {index + 1}

                                                                <input type="hidden" name={`rows[${index}][id]`} value={state.data.rows[index].id || ''} />
                                                            </td>
                                                            <td style={{ width: '25%' }}>
                                                                {row.article?.article_name}
                                                            </td>
                                                            <td style={{ width: '10%' }}>
                                                                {row.amount || 0} {row.article?.amount_type_short}
                                                            </td>
                                                            <td style={{ width: '10%' }}>
                                                                <PriceValue prefix={state.data.currency.prefix} sufix={state.data.currency.sufix}>{row.current_price}</PriceValue>
                                                            </td>
                                                            <td style={{ width: '10%' }}>
                                                                <PriceValue prefix={state.data.currency.prefix} sufix={state.data.currency.sufix}>{getRowTotalPrice(row)}</PriceValue>
                                                            </td>
                                                            <td style={{ width: '15%' }}>
                                                                {row.status?.name}
                                                            </td>
                                                            <td style={{ width: '20%' }}>
                                                                {row.notes}
                                                            </td>
                                                            <td className="options">

                                                            </td>

                                                        </tr>
                                                    )}
                                                    {/* <tr>
                                                <td colSpan={4}>
                                                    Общо
                                                </td>
                                                <td className="text-right">
                                                    {getTotalQty().toFixed(4)}
                                                </td>
                                            </tr> */}

                                                </tbody>
                                            }
                                        </Table>

                                        <Button variant="outline-dark" className="mt-3" size="sm" onClick={handleAddRow} disabled={state.data.allow_edit_rows === false}>
                                            Нов ред
                                        </Button>
                                    </div>


                                    <Row>
                                        <Col sm={4} className="mt-3">
                                            <div className="form-panel full-height">
                                                <div className="head">
                                                    Описание
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    as="textarea"
                                                    // placeholder="Бележки"
                                                    name="description"
                                                    value={state.data?.description || ''}
                                                    onChange={handleInputChange}
                                                    isInvalid={Boolean(validations?.description)}
                                                    rows={5}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {validations?.description && (validations.description[0] || validations.description)}
                                                </Form.Control.Feedback>
                                            </div>

                                        </Col>
                                        <Col sm={4} className="mt-3">
                                            <div className="form-panel full-height">
                                                <div className="head">
                                                    Бележки
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    as="textarea"
                                                    // placeholder="Бележки"
                                                    name="extra_details"
                                                    value={state.data?.extra_details || ''}
                                                    onChange={handleInputChange}
                                                    isInvalid={Boolean(validations?.extra_details)}
                                                    rows={5}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {validations?.extra_details && (validations.extra_details[0] || validations.extra_details)}
                                                </Form.Control.Feedback>
                                            </div>

                                        </Col>
                                        <Col sm={4} className="mt-3">
                                            <div className="form-panel full-height">
                                                <div className="head">
                                                    Общо
                                                </div>

                                                <Row>
                                                    <Col className="mb-3">
                                                        <Form.Group as={Row}>
                                                            <Form.Label column="sm" sm={6} className="text-left">
                                                                Общо
                                                            </Form.Label>
                                                            <Col sm={6}>
                                                                <InputGroup size="sm">
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        readOnly
                                                                        value={getTotalSum().toFixed(4)}
                                                                        size="sm"
                                                                        className="price-input"
                                                                    />
                                                                    {state.data?.currency &&
                                                                        <InputGroup.Text>
                                                                            {state.data.currency.prefix || state.data.currency.sufix || '-'}
                                                                        </InputGroup.Text>
                                                                    }
                                                                </InputGroup>
                                                            </Col>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col className="mb-3">
                                                        <Form.Group as={Row}>
                                                            <Form.Label column="sm" sm={6} className="text-left">
                                                                {isDiscount() ? 'Отстъпка' : 'Надценка'} ({state.data.discount}%)
                                                            </Form.Label>
                                                            <Col sm={6}>
                                                                <InputGroup size="sm">
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        readOnly
                                                                        value={getTotalDiscount().toFixed(4)}
                                                                        size="sm"
                                                                        className="price-input"
                                                                    />
                                                                    {state.data?.currency &&
                                                                        <InputGroup.Text>
                                                                            {state.data.currency.prefix || state.data.currency.sufix || '-'}
                                                                        </InputGroup.Text>
                                                                    }
                                                                </InputGroup>
                                                            </Col>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col className="mb-3">
                                                        <Form.Group as={Row}>
                                                            <Form.Label column="sm" sm={6} className="text-left">
                                                                Сума
                                                            </Form.Label>
                                                            <Col sm={6}>
                                                                <InputGroup size="sm">
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        readOnly
                                                                        value={getTotalReal().toFixed(4)}
                                                                        size="sm"
                                                                        className="price-input"
                                                                    />
                                                                    {state.data?.currency &&
                                                                        <InputGroup.Text>
                                                                            {state.data.currency.prefix || state.data.currency.sufix || '-'}
                                                                        </InputGroup.Text>
                                                                    }
                                                                </InputGroup>
                                                            </Col>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>


                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="buttons mt-3">
                                        <SaveButton
                                            loading={state.loadingBase}
                                            disabled={state.loading}
                                            className="save"
                                            onClick={handleSave}
                                        >
                                            Запази данните
                                        </SaveButton>
                                        {/* {state.data.allow_close &&
                                            <SaveButton
                                                loading={state.loadingClose}
                                                disabled={state.loading}
                                                className="save"
                                                onClick={handleSaveAndClose}
                                            >
                                                Запази и премести артикулите
                                            </SaveButton>
                                        } */}

                                    </div>
                                    {/* </Container> */}

                                </Form>
                            </>
                        }

                    </div>
                </div>
            </Layout>
        </>
    )
}

export default Index;